import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, ShieldCheck, RotateCw, Monitor } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // 2FA state
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resending, setResending] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const inputRefs = useRef([]);

  const { login, complete2FA } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (twoFAStep && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [twoFAStep]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.twoFARequired) {
        setSessionId(result.sessionId);
        setEmailHint(result.emailHint);
        setTwoFAStep(true);
        toast.info('Verification code sent to your email');
      } else {
        toast.success('Welcome back!');
        const redirect = searchParams.get('redirect');
        navigate(redirect || '/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...code];
    next[index] = value.slice(-1);
    setCode(next);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const next = [...code];
      for (let i = 0; i < 6; i++) next[i] = pasted[i] || '';
      setCode(next);
      const focusIdx = Math.min(pasted.length, 5);
      inputRefs.current[focusIdx]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      toast.error('Please enter the full 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await complete2FA(sessionId, fullCode, rememberDevice);
      toast.success('Welcome back!');
      const redirect = searchParams.get('redirect');
      navigate(redirect || '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await authAPI.resend2FA({ session_id: sessionId });
      toast.success('New code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    setTwoFAStep(false);
    setSessionId('');
    setEmailHint('');
    setCode(['', '', '', '', '', '']);
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24">
        {!twoFAStep ? (
          <>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground font-body text-sm mb-8 transition-colors"
              data-testid="back-to-home-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>

            <div className="flex items-center gap-3 mb-8">
              <img src="/logo.png" alt="NewSlate" className="h-12 object-contain" />
            </div>

            <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight mb-2">
              Welcome back
            </h1>
            <p className="font-body text-muted-foreground mb-8">
              Sign in to your account to continue
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-body">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="font-body bg-card"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="font-body">Password</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-primary hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="font-body bg-card"
                  data-testid="login-password-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full btn-lift bg-primary text-white font-body"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <p className="font-body text-sm text-muted-foreground mt-6 text-center">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">
                Start a free trial
              </Link>
            </p>
          </>
        ) : (
          <>
            <button
              onClick={handleBackToLogin}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground font-body text-sm mb-8 transition-colors"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-primary" />
              </div>
            </div>

            <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight mb-2">
              Verify your identity
            </h1>
            <p className="font-body text-muted-foreground mb-8">
              We sent a 6-digit code to <span className="font-medium text-foreground">{emailHint}</span>
            </p>

            <form onSubmit={handleVerify} className="space-y-6" data-testid="2fa-form">
              <div className="flex gap-3 justify-center" onPaste={handleCodePaste}>
                {code.map((digit, i) => (
                  <Input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    className="w-12 h-14 text-center text-2xl font-bold bg-card"
                    data-testid={`2fa-code-input-${i}`}
                  />
                ))}
              </div>

              {/* Remember this device */}
              <label className="flex items-center gap-3 cursor-pointer group" data-testid="remember-device-label">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    className="peer sr-only"
                    data-testid="remember-device-checkbox"
                  />
                  <div className="w-5 h-5 rounded border-2 border-muted-foreground/30 peer-checked:border-primary peer-checked:bg-primary transition-colors flex items-center justify-center">
                    {rememberDevice && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-muted-foreground" />
                  <span className="font-body text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    Remember this device for 30 days
                  </span>
                </div>
              </label>

              <Button
                type="submit"
                className="w-full btn-lift bg-primary text-white font-body"
                disabled={loading || code.join('').length !== 6}
                data-testid="2fa-verify-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Sign In'
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline disabled:opacity-50"
                  data-testid="2fa-resend-btn"
                >
                  {resending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                  Resend code
                </button>
              </div>
            </form>

            <p className="font-body text-sm text-muted-foreground mt-6 text-center">
              Code expires in 5 minutes
            </p>
          </>
        )}
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1565688527174-775059ac429c"
          alt="Professional consultation"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-display text-3xl font-bold tracking-tight mb-4">
            "NewSlate has transformed how we manage our immigration cases."
          </p>
          <p className="font-body text-white/80">
            — Immigration Law Office, New York
          </p>
        </div>
      </div>
    </div>
  );
}
